import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import api from '../api/client';
import styles from './FeatureRequestsPage.module.css';

// ── Types ────────────────────────────────────────────────────────────────────

type Priority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
type Status = 'OPEN' | 'UNDER_REVIEW' | 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED';

interface FeatureRequest {
  id: string;
  title: string;
  description: string | null;
  category: string;
  priority: Priority;
  status: Status;
  adminNote: string | null;
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

const CATEGORIES = [
  'Dashboard',
  'CRM',
  'Kalender',
  'E-Mail',
  'Aufgaben',
  'Integration',
  'Administration',
  'UI / Design',
  'Performance',
  'Sonstiges',
];

const PRIORITY_LABELS: Record<Priority, string> = {
  LOW: 'Niedrig',
  MEDIUM: 'Mittel',
  HIGH: 'Hoch',
  CRITICAL: 'Kritisch',
};

const STATUS_LABELS: Record<Status, string> = {
  OPEN: 'Offen',
  UNDER_REVIEW: 'In Pruefung',
  PLANNED: 'Geplant',
  IN_PROGRESS: 'In Umsetzung',
  COMPLETED: 'Umgesetzt',
  REJECTED: 'Abgelehnt',
};

const STATUS_COLORS: Record<Status, string> = {
  OPEN: '#3b82f6',
  UNDER_REVIEW: '#f59e0b',
  PLANNED: '#8b5cf6',
  IN_PROGRESS: '#06b6d4',
  COMPLETED: '#22c55e',
  REJECTED: '#ef4444',
};

// ── Create Modal ─────────────────────────────────────────────────────────────

function CreateModal({ onClose, onCreate }: {
  onClose: () => void;
  onCreate: (data: { title: string; description: string; category: string; priority: Priority }) => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [priority, setPriority] = useState<Priority>('MEDIUM');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    onCreate({ title: title.trim(), description: description.trim(), category, priority });
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Neuer Feature Request</h2>
          <button className={styles.modalClose} onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label}>Titel *</label>
            <input
              className={styles.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Kurze Beschreibung des Wunsches..."
              maxLength={300}
              autoFocus
              required
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Beschreibung</label>
            <textarea
              className={styles.textarea}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detaillierte Beschreibung, Anwendungsfall, Erwartungen..."
              rows={5}
            />
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.label}>Kategorie</label>
              <select
                className={styles.select}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Prioritaet</label>
              <select
                className={styles.select}
                value={priority}
                onChange={(e) => setPriority(e.target.value as Priority)}
              >
                {(Object.keys(PRIORITY_LABELS) as Priority[]).map((p) => (
                  <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.modalActions}>
            <button type="button" className={styles.btnSecondary} onClick={onClose}>
              Abbrechen
            </button>
            <button type="submit" className={styles.btnPrimary} disabled={!title.trim()}>
              Absenden
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export function FeatureRequestsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [filterStatus, setFilterStatus] = useState<Status | 'ALL'>('ALL');

  const isAdmin = user?.role === 'PLATFORM_ADMIN' || user?.role === 'TENANT_ADMIN';

  const { data, isLoading } = useQuery<FeatureRequest[]>({
    queryKey: ['feature-requests'],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: FeatureRequest[] }>(
        '/feature-requests',
      );
      return res.data.data;
    },
    staleTime: 60_000,
  });

  const createMutation = useMutation({
    mutationFn: (body: { title: string; description: string; category: string; priority: Priority }) =>
      api.post('/feature-requests', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['feature-requests'] });
      setShowCreate(false);
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status, adminNote }: { id: string; status: Status; adminNote?: string }) =>
      api.patch(`/feature-requests/${id}/status`, { status, adminNote }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['feature-requests'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/feature-requests/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['feature-requests'] });
    },
  });

  const requests = data ?? [];
  const filtered = filterStatus === 'ALL'
    ? requests
    : requests.filter((r) => r.status === filterStatus);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Feature Requests</h1>
          <p className={styles.subtitle}>
            Teilen Sie Ihre Ideen und Verbesserungsvorschlaege mit uns.
          </p>
        </div>
        <button className={styles.btnPrimary} onClick={() => setShowCreate(true)}>
          + Neuer Request
        </button>
      </div>

      {/* Filter */}
      <div className={styles.filters}>
        <button
          className={`${styles.filterBtn} ${filterStatus === 'ALL' ? styles.filterBtnActive : ''}`}
          onClick={() => setFilterStatus('ALL')}
        >
          Alle ({requests.length})
        </button>
        {(Object.keys(STATUS_LABELS) as Status[]).map((s) => {
          const count = requests.filter((r) => r.status === s).length;
          if (count === 0 && !isAdmin) return null;
          return (
            <button
              key={s}
              className={`${styles.filterBtn} ${filterStatus === s ? styles.filterBtnActive : ''}`}
              onClick={() => setFilterStatus(s)}
            >
              {STATUS_LABELS[s]} ({count})
            </button>
          );
        })}
      </div>

      {/* Liste */}
      {isLoading && <p className={styles.loading}>Laden...</p>}

      {!isLoading && filtered.length === 0 && (
        <div className={styles.empty}>
          <span className={styles.emptyIcon}>💡</span>
          <p className={styles.emptyTitle}>Noch keine Feature Requests</p>
          <p className={styles.emptySub}>
            Erstellen Sie den ersten Vorschlag!
          </p>
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <div className={styles.list}>
          {filtered.map((req) => (
            <div key={req.id} className={styles.card}>
              <div className={styles.cardHeader}>
                <span
                  className={styles.statusBadge}
                  style={{ background: STATUS_COLORS[req.status] + '18', color: STATUS_COLORS[req.status], borderColor: STATUS_COLORS[req.status] + '40' }}
                >
                  {STATUS_LABELS[req.status]}
                </span>
                <span className={styles.categoryBadge}>{req.category}</span>
                <span className={`${styles.priorityBadge} ${styles[`priority_${req.priority}`]}`}>
                  {PRIORITY_LABELS[req.priority]}
                </span>
              </div>

              <h3 className={styles.cardTitle}>{req.title}</h3>

              {req.description && (
                <p className={styles.cardDesc}>{req.description}</p>
              )}

              {req.adminNote && (
                <div className={styles.adminNote}>
                  <strong>Admin:</strong> {req.adminNote}
                </div>
              )}

              <div className={styles.cardFooter}>
                <span className={styles.cardMeta}>
                  {req.user.firstName} {req.user.lastName} &middot;{' '}
                  {new Date(req.createdAt).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })}
                </span>

                <div className={styles.cardActions}>
                  {/* Admin: Status aendern */}
                  {isAdmin && (
                    <select
                      className={styles.statusSelect}
                      value={req.status}
                      onChange={(e) =>
                        statusMutation.mutate({
                          id: req.id,
                          status: e.target.value as Status,
                        })
                      }
                    >
                      {(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
                        <option key={s} value={s}>
                          {STATUS_LABELS[s]}
                        </option>
                      ))}
                    </select>
                  )}

                  {/* Löschen (Admin oder Ersteller) */}
                  {(isAdmin || req.user.id === user?.id) && (
                    <button
                      className={styles.deleteBtn}
                      onClick={() => {
                        if (confirm('Feature Request wirklich löschen?')) {
                          deleteMutation.mutate(req.id);
                        }
                      }}
                      title="Löschen"
                    >
                      🗑
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreate={(data) => createMutation.mutate(data)}
        />
      )}
    </div>
  );
}
